import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ethers } from 'ethers';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  LinearProgress,
  Alert,
  Divider,
  FormControl,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CheckCircle,
  ArrowForward,
  ArrowBack,
  Download,
  Upload,
  Refresh,
  Launch,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Connection } from '@solana/web3.js';

import { useWallet } from '../hooks/useWallet';
import { useTronWallet } from '../hooks/useTronWallet';
import tronInternullService from '../services/tronInternullService';
import { useThemeMode } from '../hooks/useThemeMode';
import { useWallet as useSolWallet, useConnection } from '@solana/wallet-adapter-react';
import useTokenMappings from '../hooks/useTokenMappings';
import multiTokenTreasuryService from '../services/multiTokenTreasuryService';
import { getChainType, buildExplorerTxUrl } from '../utils/chainDetect';
import solanaInternullService from '../services/solanaInternullService';
import depositTracker from '../services/depositTracker';
import {
  requestKeysOnly,
  getAvailableDenominations,
  executeRelayedWithdrawal,
  executeSolanaRelayedWithdrawal,
} from '../services/dkgSimpleWithdrawal';
import { getV2Status } from '../services/dkgKeyshareService';
import { getRelayInfo, estimateMaxRelayerFee, estimateRealisticRelayFee } from '../services/relayService';
import singleNodeWithdrawal from '../services/singleNodeWithdrawal';
import { getChainLogoUrl } from '../components/ChainLogos';
import { getMetaMaskProvider } from '../utils/contractInteraction';
import KeyEncryptionService from '../utils/keyEncryption';
import { prepareKeysForDownload } from '../utils/downloadHelper';
import { processUploadedKeys } from '../utils/uploadHelper';
import KeyConfigForm from '../components/KeyConfigForm';
import WithdrawalDialog from '../components/WithdrawalDialog';
import SimpleWithdrawalDialog from '../components/SimpleWithdrawalDialog';
import PasswordDialog from '../components/PasswordDialog';
import SuccessDialog from '../components/SuccessDialog';

// Block times in seconds per block for confirmation estimates
const BLOCK_TIMES = {
  11155111: 12, // Sepolia
  1: 12,        // Ethereum mainnet
  97: 3,        // BNB testnet
  56: 3,        // BNB mainnet
  84532: 1,     // Base Sepolia
  8453: 1,      // Base mainnet
  137: 2,       // Polygon mainnet
  80001: 2,     // Polygon Mumbai
  998: 1,       // Hyperliquid testnet
  999: 1,       // Hyperliquid mainnet
  728126428: 3,    // Tron mainnet
  2494104990: 3,   // Tron Shasta
  3448148188: 3,   // Tron Nile testnet
};
const REQUIRED_CONFIRMATIONS = 2;
const CONFIRMATION_POLL_INTERVAL = 2000; // ms

const STEPS = ['Setup', 'Lock', 'Confirming', 'Withdrawing'];

const EasyMode = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isDark } = useThemeMode();
  const {
    account, provider, signer, connected, chainId,
    connectWallet: _connectWallet,
  } = useWallet();
  const solanaWallet = useSolWallet();
  const { publicKey: solanaPublicKey, connected: solanaConnected, sendTransaction: solanaSendTransaction, signTransaction: solanaSignTransaction } = solanaWallet;
  // Tron wallet — TronLink only. Used ONLY when isTronDeposit is true.
  const tronWallet = useTronWallet();
  const { address: tronAddress, connected: tronConnected, tronWeb, connect: connectTron } = tronWallet;
  const { connection: solanaConnection } = useConnection();
  const { mappings: _tokenMappings, loading: _mappingsLoading } = useTokenMappings();

  // Phase tracking
  const [phase, setPhase] = useState(0);

  // Phase 0: Configuration
  const [selectedToken, setSelectedToken] = useState('');
  const [keyRows, setKeyRows] = useState([{ chain: '', denomination: '', recipientAddress: '' }]);
  const [depositChainId, setDepositChainId] = useState('');
  const [supportedChains, setSupportedChains] = useState([]); // backend format with chain_name etc.
  const [formattedChains, setFormattedChains] = useState([]); // UI format with chainId, name, symbol
  const [tokensByChain, setTokensByChain] = useState({}); // { chainId: [{ address, symbol, ... }] }
  const [denomsByChain, setDenomsByChain] = useState({});
  const [allTokenSymbols, setAllTokenSymbols] = useState([]);

  // Phase 1: Deposit
  const [depositLoading, setDepositLoading] = useState(false);
  const [txHash, setTxHash] = useState('');

  // Phase 2: Confirming
  const [confirmations, setConfirmations] = useState(0);
  const [confirmationStatus, setConfirmationStatus] = useState('waiting'); // waiting|confirmed|requesting_keys|signing|done|error
  const [keyRequestProgress, setKeyRequestProgress] = useState(null);
  const [confirmError, setConfirmError] = useState(null);
  const confirmPollRef = useRef(null);

  // Phase 3: Complete
  const [reconstructedKeys, setReconstructedKeys] = useState([]);
  // Auto-withdrawal tracking: [{ keyIndex, status: 'pending'|'withdrawing'|'success'|'failed', error?, txHash? }]
  const [autoWithdrawStatuses, setAutoWithdrawStatuses] = useState(null);
  const autoWithdrawRunningRef = useRef(false);

  // Withdrawal dialog (reusing existing component)
  const [withdrawDialog, setWithdrawDialog] = useState(false);
  const [withdrawDeposit, setWithdrawDeposit] = useState(null);
  const [availableKeys, setAvailableKeys] = useState([]);
  const [selectedKeyIndex, setSelectedKeyIndex] = useState(null);
  const [recipientAddress, setRecipientAddress] = useState('');
  const [withdrawalMode, setWithdrawalMode] = useState('gasfree'); // manual 'direct' removed from UI; all withdrawals relay
  const [relayInfo, setRelayInfo] = useState(null);
  const [relayerHasBalance, setRelayerHasBalance] = useState(true);
  const [withdrawing, setWithdrawing] = useState(false);
  const [relayJobId, setRelayJobId] = useState(null);
  const [relayNodeUrl, setRelayNodeUrl] = useState(null);
  const [expandedChains, setExpandedChains] = useState({});
  const [expandedTokens, setExpandedTokens] = useState({});

  // Password dialog for key download/upload
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [passwordDialogMode, setPasswordDialogMode] = useState('download'); // 'download' or 'upload'
  const fileInputRef = useRef(null);
  const encryptedDataRef = useRef(null);

  // Success dialog
  const [successDialog, setSuccessDialog] = useState(false);
  const [successDetails, setSuccessDetails] = useState({ title: '', message: '', txHash: '', explorerUrl: '', chainName: '' });

  // DKG status
  const [dkgV2Ready, setDkgV2Ready] = useState(false);

  // Styling helpers
  const borderColor = isDark ? '#262626' : '#e5e5e5';
  const subtleText = isDark ? '#a3a3a3' : '#525252';
  const subtleBg = isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)';
  const cardBg = isDark ? '#0a0a0a' : '#ffffff';


  // ---------- Data Loading ----------

  // Load chains + tokens on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        await multiTokenTreasuryService.loadChainsFromBackend();
        const chains = await multiTokenTreasuryService.getSupportedChains();

        const formatted = chains.map((chain) => ({
          chainId: chain.chainId,
          name: chain.name,
          chain_name: chain.chainName || chain.chain_name,
          symbol: chain.nativeCurrency || 'ETH',
          treasuryAddress: chain.treasuryAddress,
          chainType: chain.chainType || 'evm',
          supported_tokens: chain.supported_tokens || [],
        }));

        setSupportedChains(chains);
        setFormattedChains(formatted);

        // Load tokens for each chain
        const tokenMap = {};
        const symbolSet = new Set();
        for (const chain of formatted) {
          try {
            const tokens = await multiTokenTreasuryService.getSupportedTokens(chain.chainId);
            tokenMap[chain.chainId] = tokens;
            tokens.forEach((t) => symbolSet.add(t.symbol));
          } catch (err) {
            console.warn(`Failed to load tokens for chain ${chain.name}:`, err);
          }
        }
        setTokensByChain(tokenMap);
        setAllTokenSymbols([...symbolSet]);
      } catch (error) {
        console.error('Failed to load chain data:', error);
        toast.error('Failed to load chain data. Please refresh.');
      }
    };
    loadData();
  }, []);

  // Load denominations for chains when they appear in keyRows
  useEffect(() => {
    const chainsToLoad = keyRows
      .map((r) => r.chain)
      .filter((c) => c && !denomsByChain[c]);
    const unique = [...new Set(chainsToLoad)];

    unique.forEach(async (chainName) => {
      try {
        const denoms = await getAvailableDenominations(chainName);
        setDenomsByChain((prev) => ({ ...prev, [chainName]: denoms }));
      } catch (err) {
        console.warn(`Failed to load denominations for ${chainName}:`, err);
      }
    });
  }, [keyRows, denomsByChain]);

  // Check DKG nodes on mount
  useEffect(() => {
    const checkDkg = async () => {
      try {
        const statuses = await getV2Status();
        const readyCount = statuses.filter((s) => s.healthy && s.masterInitialized).length;
        setDkgV2Ready(readyCount >= 3);
      } catch (err) {
        console.warn('DKG status check failed:', err);
      }
    };
    checkDkg();
  }, []);

  // Fetch relay info for gas-free withdrawals
  useEffect(() => {
    getRelayInfo().then(info => {
      setRelayInfo(info);
      if (info) console.log('Relay info loaded:', info.evm_relayer_address);
    }).catch(err => console.warn('Relay info fetch failed:', err.message));
  }, []);

  // Check relayer on-chain balance when relay info is available
  useEffect(() => {
    if (!relayInfo) return;
    let cancelled = false;
    const checkBalance = async () => {
      try {
        if (relayInfo.evm_relayer_address && provider) {
          const balance = await provider.getBalance(relayInfo.evm_relayer_address);
          if (!cancelled) setRelayerHasBalance(!balance.isZero());
        } else if (relayInfo.solana_relayer_address && solanaConnection) {
          const { PublicKey } = await import('@solana/web3.js');
          const balance = await solanaConnection.getBalance(new PublicKey(relayInfo.solana_relayer_address));
          if (!cancelled) setRelayerHasBalance(balance > 0);
        }
      } catch (e) {
        console.warn('Relayer balance check failed:', e.message);
      }
    };
    checkBalance();
    return () => { cancelled = true; };
  }, [relayInfo, provider, solanaConnection]);

  // ---------- Derived State ----------

  // Chains that have the selected token
  const chainsForToken = useMemo(() => {
    if (!selectedToken) return [];
    return formattedChains.filter((chain) => {
      const tokens = tokensByChain[chain.chainId] || [];
      return tokens.some((t) => t.symbol === selectedToken);
    });
  }, [selectedToken, formattedChains, tokensByChain]);

  // Deposit chain options — chains where the selected token is available
  const depositChainOptions = useMemo(() => {
    return chainsForToken;
  }, [chainsForToken]);

  // Total deposit amount
  const totalAmount = useMemo(() => {
    return keyRows.reduce((sum, r) => sum + (parseFloat(r.denomination) || 0), 0);
  }, [keyRows]);

  // Token is locked once at least one row has a chain selected
  const tokenLocked = useMemo(() => {
    return keyRows.some((r) => r.chain || r.denomination);
  }, [keyRows]);

  // Deposit chain info
  const depositChainInfo = useMemo(() => {
    return formattedChains.find((c) => c.chainId === depositChainId);
  }, [formattedChains, depositChainId]);

  const isSolanaDeposit = useMemo(() => {
    if (!depositChainInfo) return false;
    return (
      depositChainInfo.chainType === 'solana' ||
      depositChainInfo.name?.toLowerCase().includes('solana') ||
      (typeof depositChainId === 'string' && depositChainId.toLowerCase().includes('solana'))
    );
  }, [depositChainInfo, depositChainId]);

  const isTronDeposit = useMemo(() => {
    if (!depositChainInfo) return false;
    return (
      depositChainInfo.chainType === 'tron' ||
      depositChainInfo.name?.toLowerCase().startsWith('tron') ||
      (typeof depositChainId === 'string' && depositChainId.toString().toLowerCase().startsWith('tron'))
    );
  }, [depositChainInfo, depositChainId]);

  // Validation for Phase 0
  const canContinueToDeposit = useMemo(() => {
    if (!selectedToken) return false;
    if (!depositChainId) return false;
    if (totalAmount <= 0) return false;
    const validRows = keyRows.filter((r) => r.chain && r.denomination && parseFloat(r.denomination) > 0 && r.recipientAddress?.trim());
    return validRows.length > 0;
  }, [selectedToken, depositChainId, totalAmount, keyRows]);

  // ---------- Handlers ----------

  const handleTokenChange = (token) => {
    setSelectedToken(token);
    setKeyRows([{ chain: '', denomination: '', recipientAddress: '' }]);
    setDepositChainId('');

    // Preload denominations for all chains that have this token
    const relevantChains = formattedChains.filter((chain) => {
      const tokens = tokensByChain[chain.chainId] || [];
      return tokens.some((t) => t.symbol === token);
    });
    relevantChains.forEach(async (chain) => {
      const chainName = chain.chain_name;
      if (chainName && !denomsByChain[chainName]) {
        try {
          const denoms = await getAvailableDenominations(chainName);
          setDenomsByChain((prev) => ({ ...prev, [chainName]: denoms }));
        } catch (err) {
          console.warn(`Failed to load denominations for ${chainName}`);
        }
      }
    });
  };

  const handleUpdateRow = (index, field, value) => {
    setKeyRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      // Reset denomination and recipient when chain changes
      if (field === 'chain') {
        updated[index].denomination = '';
        updated[index].recipientAddress = '';
      }
      return updated;
    });
  };

  const handleAddRow = () => {
    setKeyRows((prev) => [...prev, { chain: '', denomination: '', recipientAddress: '' }]);
  };

  const handleRemoveRow = (index) => {
    setKeyRows((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      return updated.length === 0 ? [{ chain: '', denomination: '', recipientAddress: '' }] : updated;
    });
  };

  // ---------- Phase 1: Deposit Execution ----------

  const ensureEvmNetwork = async () => {
    if (chainId === depositChainId) {
      const treasuryAddress = multiTokenTreasuryService.getContractAddressForChain(depositChainId);
      if (!treasuryAddress) throw new Error(`Treasury contract not configured for chain ${depositChainId}`);
      await multiTokenTreasuryService.initialize(provider, signer, treasuryAddress);
      return;
    }

    const metamaskProvider = getMetaMaskProvider();
    if (!metamaskProvider) {
      throw new Error('MetaMask not found. Please install MetaMask.');
    }

    const refreshAndInitTreasury = async (mmProvider) => {
      // Poll eth_chainId until MetaMask reports the target chain (up to ~12s)
      // instead of a fixed 2s delay, which raced with MetaMask's switch and
      // could init the treasury before the network actually changed.
      const readChainId = async () => {
        try {
          return parseInt(await mmProvider.request({ method: 'eth_chainId' }), 16);
        } catch (_) {
          try {
            return (await new ethers.providers.Web3Provider(mmProvider).getNetwork()).chainId;
          } catch (_) {
            return null;
          }
        }
      };
      let curChainId = await readChainId();
      for (let i = 0; i < 40 && curChainId !== depositChainId; i++) {
        await new Promise((resolve) => setTimeout(resolve, 300));
        curChainId = await readChainId();
      }
      const newProvider = new ethers.providers.Web3Provider(mmProvider);
      const newSigner = newProvider.getSigner();
      const treasuryAddress = multiTokenTreasuryService.getContractAddressForChain(depositChainId);
      if (!treasuryAddress) throw new Error(`Treasury contract not configured for chain ${depositChainId}`);
      await multiTokenTreasuryService.initialize(newProvider, newSigner, treasuryAddress);
    };

    const rpcUrls = {
      97: ['https://bsc-testnet-rpc.publicnode.com'],
      56: ['https://bsc-dataseed.binance.org'],
      11155111: ['https://ethereum-sepolia-rpc.publicnode.com'],
      1: ['https://ethereum-rpc.publicnode.com'],
      137: ['https://polygon-rpc.com'],
      80001: ['https://rpc-mumbai.maticvigil.com'],
      84532: ['https://sepolia.base.org'],
      8453: ['https://mainnet.base.org'],
      998: ['https://api.hyperliquid-testnet.xyz/evm'],
      999: ['https://api.hyperliquid.xyz/evm'],
    };
    const explorers = {
      97: ['https://testnet.bscscan.com'], 56: ['https://bscscan.com'],
      11155111: ['https://sepolia.etherscan.io'], 1: ['https://etherscan.io'],
      137: ['https://polygonscan.com'], 80001: ['https://mumbai.polygonscan.com'],
      84532: ['https://sepolia.basescan.org'], 8453: ['https://basescan.org'],
      998: ['https://explorer.hyperliquid-testnet.xyz'], 999: ['https://explorer.hyperliquid.xyz'],
    };

    try {
      await metamaskProvider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${depositChainId.toString(16)}` }],
      });
      await refreshAndInitTreasury(metamaskProvider);
    } catch (switchError) {
      if (switchError.code === 4902 || switchError.message?.includes('not connected to the requested chain')) {
        const chainInfo = depositChainInfo;
        if (!chainInfo) throw new Error(`Chain ${depositChainId} not found in configuration`);
        try {
          await metamaskProvider.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: `0x${depositChainId.toString(16)}`,
              chainName: chainInfo.name,
              nativeCurrency: { name: chainInfo.symbol, symbol: chainInfo.symbol, decimals: 18 },
              rpcUrls: rpcUrls[depositChainId] || [],
              blockExplorerUrls: explorers[depositChainId] || [],
            }],
          });
          await refreshAndInitTreasury(metamaskProvider);
        } catch (addError) {
          throw new Error(`Please add ${chainInfo.name} to MetaMask manually and try again`);
        }
      } else if (switchError.code === 4001) {
        throw new Error('Network switch rejected by user');
      } else {
        throw new Error(`Failed to switch to ${depositChainInfo?.name || depositChainId}. Please switch manually.`);
      }
    }
  };

  const executeDeposit = async () => {
    try {
      setDepositLoading(true);

      // Find the token address on the deposit chain
      const chainTokens = tokensByChain[depositChainId] || [];
      const tokenInfo = chainTokens.find((t) => t.symbol === selectedToken);
      if (!tokenInfo) {
        throw new Error(`${selectedToken} not found on deposit chain`);
      }

      if (isSolanaDeposit) {
        // Solana deposit
        if (!solanaConnected || !solanaPublicKey) {
          throw new Error('Please connect your Solana wallet first');
        }

        let connectionToUse = solanaConnection;
        const backendRpcUrl = multiTokenTreasuryService.getRpcUrlByChainName('solana-devnet');
        if (backendRpcUrl) {
          connectionToUse = new Connection(backendRpcUrl, 'confirmed');
        }

        // Check Solana wallet balance before depositing
        const isNativeSOL = tokenInfo.isNative || tokenInfo.symbol === 'SOL';
        try {
          if (isNativeSOL) {
            const { LAMPORTS_PER_SOL } = await import('@solana/web3.js');
            const lamports = await connectionToUse.getBalance(solanaPublicKey);
            const solBalance = lamports / LAMPORTS_PER_SOL;
            // Reserve ~0.01 SOL for tx fees
            const availableBalance = Math.max(0, solBalance - 0.01);
            if (totalAmount > availableBalance) {
              throw new Error(
                `Insufficient SOL balance. You have ${solBalance.toFixed(4)} SOL (${availableBalance.toFixed(4)} available after fees) but need ${totalAmount} SOL.`
              );
            }
          } else {
            const { PublicKey: SolPublicKey } = await import('@solana/web3.js');
            const { TOKEN_PROGRAM_ID } = await import('@solana/spl-token');
            const mintPubkey = new SolPublicKey(tokenInfo.address);
            const tokenAccounts = await connectionToUse.getTokenAccountsByOwner(solanaPublicKey, {
              programId: TOKEN_PROGRAM_ID,
            });
            let tokenBalance = 0;
            for (const { account } of tokenAccounts.value) {
              const data = account.data;
              // SPL token account layout: mint (32 bytes) at offset 0
              const mint = new SolPublicKey(data.slice(0, 32));
              if (mint.equals(mintPubkey)) {
                // amount is a u64 at offset 64
                const rawAmount = data.readBigUInt64LE(64);
                const decimals = tokenInfo.decimals || 6;
                tokenBalance += Number(rawAmount) / Math.pow(10, decimals);
              }
            }
            if (totalAmount > tokenBalance) {
              throw new Error(
                `Insufficient ${selectedToken} balance. You have ${tokenBalance.toFixed(4)} ${selectedToken} but need ${totalAmount} ${selectedToken}.`
              );
            }
          }
        } catch (balanceErr) {
          if (balanceErr.message.includes('Insufficient')) throw balanceErr;
          console.warn('Could not check Solana balance (proceeding):', balanceErr.message);
        }

        await solanaInternullService.initialize(connectionToUse, {
          publicKey: solanaPublicKey,
          sendTransaction: solanaSendTransaction,
          signTransaction: solanaSignTransaction,
        });

        let result;
        if (isNativeSOL) {
          result = await solanaInternullService.depositNative(totalAmount);
        } else {
          result = await solanaInternullService.depositToken(totalAmount, tokenInfo.address, tokenInfo.decimals);
        }

        if (!result?.success) throw new Error('Solana deposit failed');
        setTxHash(result.txHash);

        depositTracker.addDeposit(
          result.txHash, totalAmount, solanaPublicKey.toString(), 'solana-devnet',
          { token: selectedToken, tokenAddress: isNativeSOL ? 'native' : tokenInfo.address }
        );

        // Confirmation already handled inside solanaInternullService (polling-based, no WebSocket)

        depositTracker.updateDepositStatus(result.txHash, 'confirmed');
        toast.success('Deposit confirmed!');
        setPhase(2);
        setConfirmationStatus('confirmed');
        // For Solana, skip block confirmation polling — go straight to key request
        setTimeout(() => requestKeysAfterConfirmation(result.txHash), 500);
      } else if (isTronDeposit) {
        // Tron deposit — TronLink only. Never touches MetaMask or Phantom.
        if (!tronConnected || !tronAddress || !tronWeb) {
          try { await connectTron(); } catch (e) {
            throw new Error('Please connect your TronLink wallet first');
          }
        }
        const chainInfo = formattedChains.find((c) => c.chainId === depositChainId);
        const treasuryAddress = chainInfo?.treasuryAddress;
        if (!treasuryAddress) throw new Error('Treasury not configured for this Tron chain');
        const isNativeTRX = tokenInfo.isNative || tokenInfo.symbol === 'TRX' || (tokenInfo.address === '0x0000000000000000000000000000000000000000');
        if (!isNativeTRX) throw new Error('TRC-20 deposits not supported yet — use native TRX');

        const result = await tronInternullService.depositNative(tronWeb, treasuryAddress, totalAmount);
        if (!result?.success) throw new Error('Tron deposit failed');

        setTxHash(result.txHash);
        depositTracker.addDeposit(
          result.txHash, totalAmount, tronAddress, depositChainId,
          { token: selectedToken, tokenAddress: 'native' },
        );
        depositTracker.updateDepositStatus(result.txHash, 'confirmed');
        toast.success('Tron deposit submitted! Waiting for confirmations...');
        setPhase(2);
        setConfirmationStatus('waiting');
        // Same path as EVM: poll until DKG node sees enough confirmations.
        startConfirmationPolling(result.txHash);
      } else {
        // EVM deposit — single deposit of total amount (EVM contracts accept arbitrary amounts)
        if (!signer || !connected) {
          throw new Error('Please connect your EVM wallet first');
        }

        await ensureEvmNetwork();

        // Check EVM wallet balance before depositing
        const isNativeEth = !tokenInfo.address || tokenInfo.address === '0x0000000000000000000000000000000000000000';
        try {
          // Get a fresh provider/signer after ensureEvmNetwork (may have switched chains)
          const metamaskProv = getMetaMaskProvider();
          const currentProvider = metamaskProv
            ? new ethers.providers.Web3Provider(metamaskProv)
            : provider;
          const currentAccount = await currentProvider.getSigner().getAddress();

          if (isNativeEth) {
            const balanceWei = await currentProvider.getBalance(currentAccount);
            const balanceEth = parseFloat(ethers.utils.formatEther(balanceWei));
            // Reserve ~0.005 ETH for gas
            const available = Math.max(0, balanceEth - 0.005);
            if (totalAmount > available) {
              throw new Error(
                `Insufficient balance. You have ${balanceEth.toFixed(6)} ${selectedToken} (${available.toFixed(6)} available after gas) but need ${totalAmount} ${selectedToken}.`
              );
            }
          } else {
            const erc20 = new ethers.Contract(
              tokenInfo.address,
              ['function balanceOf(address) view returns (uint256)', 'function decimals() view returns (uint8)'],
              currentProvider
            );
            const [rawBalance, decimals] = await Promise.all([
              erc20.balanceOf(currentAccount),
              erc20.decimals().catch(() => tokenInfo.decimals || 18),
            ]);
            const tokenBalance = parseFloat(ethers.utils.formatUnits(rawBalance, decimals));
            if (totalAmount > tokenBalance) {
              throw new Error(
                `Insufficient ${selectedToken} balance. You have ${tokenBalance.toFixed(4)} ${selectedToken} but need ${totalAmount} ${selectedToken}.`
              );
            }
          }
        } catch (balanceErr) {
          if (balanceErr.message.includes('Insufficient')) throw balanceErr;
          console.warn('Could not check EVM balance (proceeding):', balanceErr.message);
        }

        const result = await multiTokenTreasuryService.deposit(tokenInfo.address, totalAmount);
        if (!result?.success) throw new Error('Deposit failed');

        setTxHash(result.txHash);

        depositTracker.addDeposit(
          result.txHash, totalAmount, account, depositChainId,
          { token: selectedToken, tokenAddress: tokenInfo.address }
        );
        depositTracker.updateDepositStatus(result.txHash, 'confirmed');

        toast.success('Deposit submitted! Waiting for confirmations...');
        setPhase(2);
        setConfirmationStatus('waiting');
        // Start polling for block confirmations
        startConfirmationPolling(result.txHash);
      }
    } catch (error) {
      console.error('Deposit error:', error);
      toast.error(error.message || 'Deposit failed');
    } finally {
      setDepositLoading(false);
    }
  };

  // ---------- Phase 2: Block Confirmation Polling ----------

  const startConfirmationPolling = useCallback((depositTxHash) => {
    // Clear any existing poller
    if (confirmPollRef.current) clearInterval(confirmPollRef.current);

    const isTronChain = isTronDeposit; // captured from the closure when polling starts

    // Tron-specific polling via TronGrid HTTP. Ethers JsonRpcProvider can't talk
    // to TronGrid (no eth_* methods), so without this branch the polling hangs
    // indefinitely and key request never auto-triggers.
    const pollTron = async () => {
      try {
        const rpcUrl = multiTokenTreasuryService.getRpcUrlForChain(depositChainId)
          || 'https://nile.trongrid.io';
        // 1. Fetch tx info (has blockNumber once mined)
        const infoResp = await fetch(`${rpcUrl.replace(/\/$/, '')}/wallet/gettransactioninfobyid`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ value: depositTxHash.replace(/^0x/, '') }),
        });
        const info = await infoResp.json();
        const txBlock = info && info.blockNumber;
        if (!txBlock) return; // not mined yet

        // 2. Fetch current head block
        const headResp = await fetch(`${rpcUrl.replace(/\/$/, '')}/wallet/getnowblock`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: '{}',
        });
        const head = await headResp.json();
        const headBlock = head?.block_header?.raw_data?.number;
        if (!headBlock) return;

        const confs = headBlock - txBlock;
        setConfirmations(Math.max(0, confs));

        if (confs >= REQUIRED_CONFIRMATIONS) {
          clearInterval(confirmPollRef.current);
          confirmPollRef.current = null;
          setConfirmationStatus('confirmed');
          toast.success('Deposit confirmed! Requesting keys...');
          setTimeout(() => requestKeysAfterConfirmation(depositTxHash), 1000);
        }
      } catch (err) {
        console.warn('Tron confirmation polling error:', err);
      }
    };

    const pollEvm = async () => {
      try {
        // Use the deposit chain's RPC if we're on a different network
        let pollProvider = provider;
        const rpcUrl = multiTokenTreasuryService.getRpcUrlForChain(depositChainId);
        if (rpcUrl) {
          pollProvider = new ethers.providers.JsonRpcProvider(rpcUrl);
        }

        const receipt = await pollProvider.getTransactionReceipt(depositTxHash);
        if (!receipt) return; // TX not mined yet

        const currentBlock = await pollProvider.getBlockNumber();
        const confs = currentBlock - receipt.blockNumber;
        setConfirmations(Math.max(0, confs));

        if (confs >= REQUIRED_CONFIRMATIONS) {
          clearInterval(confirmPollRef.current);
          confirmPollRef.current = null;
          setConfirmationStatus('confirmed');
          toast.success('Deposit confirmed! Requesting keys...');
          // Auto-trigger key request
          setTimeout(() => requestKeysAfterConfirmation(depositTxHash), 1000);
        }
      } catch (err) {
        console.warn('Confirmation polling error:', err);
      }
    };

    const poll = isTronChain ? pollTron : pollEvm;

    // Poll immediately, then at interval
    poll();
    confirmPollRef.current = setInterval(poll, CONFIRMATION_POLL_INTERVAL);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, depositChainId, isTronDeposit]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (confirmPollRef.current) clearInterval(confirmPollRef.current);
    };
  }, []);

  // ---------- Phase 2: Key Request ----------

  const requestKeysAfterConfirmation = async (depositTxHash) => {
    setConfirmationStatus('signing');
    setKeyRequestProgress({ step: 'signing', message: 'Please sign the message in your wallet...' });

    try {
      // Resolve source chain name
      let sourceChainName;
      if (isSolanaDeposit) {
        sourceChainName = 'solana-devnet';
      } else if (isTronDeposit) {
        const chainInfo = formattedChains.find((c) => c.chainId === depositChainId);
        sourceChainName = chainInfo?.chain_name || 'tron-nile';
      } else {
        const chainInfo = formattedChains.find((c) => c.chainId === depositChainId);
        sourceChainName = chainInfo?.chain_name || 'sepolia';
      }

      // Build withdrawal requests
      const withdrawalRequests = keyRows
        .filter((r) => r.chain && r.denomination && parseFloat(r.denomination) > 0)
        .map((r) => ({
          denomination: r.denomination,
          target_chain: r.chain,
          token_symbol: selectedToken,
        }));

      setConfirmationStatus('requesting_keys');

      const result = await requestKeysOnly({
        depositTxHash: depositTxHash.trim(),
        sourceChain: sourceChainName,
        withdrawalRequests,
        // Pass ONLY the wallet matching the source chain; others null.
        signer: (isSolanaDeposit || isTronDeposit) ? null : signer,
        solanaWallet: isSolanaDeposit ? solanaWallet : null,
        tronWallet: isTronDeposit ? tronWallet : null,
        isSolana: isSolanaDeposit,
        isTron: isTronDeposit,
        occurrenceOffset: 0,
      }, (progress) => {
        setKeyRequestProgress(progress);
      });

      if (result.success) {
        setReconstructedKeys(result.keys);
        setConfirmationStatus('done');

        // Update deposit tracker (strip private keys — they must never be stored in localStorage)
        depositTracker.updateDepositStatus(depositTxHash, 'keys_received', {
          requestId: `dkg-${Date.now()}`,
          keys: result.keys,
        });

        toast.success('Keys generated! Starting auto-withdrawal...');
        setPhase(3);

        // Start auto-withdrawal if relay is available and we have recipient addresses
        const hasRecipients = keyRows.every((r) => r.recipientAddress?.trim());
        if (relayInfo && hasRecipients) {
          setTimeout(() => startAutoWithdrawal(result.keys), 500);
        }
      } else {
        throw new Error(result.error || 'Key request failed');
      }
    } catch (error) {
      console.error('Key request error:', error);
      setConfirmationStatus('error');
      setConfirmError(error.message || 'Failed to generate keys');
      toast.error(error.message || 'Key request failed');
    }
  };

  // Retry key request after error
  const retryKeyRequest = () => {
    if (txHash) {
      setConfirmError(null);
      requestKeysAfterConfirmation(txHash);
    }
  };

  // ---------- Phase 3: Auto-Withdrawal ----------

  const startAutoWithdrawal = async (keys) => {
    if (autoWithdrawRunningRef.current) return;
    autoWithdrawRunningRef.current = true;

    // Build initial statuses — match keys to keyRows by index
    const validRows = keyRows.filter((r) => r.chain && r.denomination && parseFloat(r.denomination) > 0 && r.recipientAddress?.trim());
    const statuses = keys.map((key, i) => ({
      keyIndex: key.key_index ?? key.keyIndex,
      recipient: validRows[i]?.recipientAddress?.trim() || '',
      status: 'pending',
      error: null,
      txHash: null,
    }));
    setAutoWithdrawStatuses(statuses);

    // Process keys sequentially
    for (let i = 0; i < keys.length; i++) {
      const recipient = statuses[i].recipient;
      if (!recipient) {
        setAutoWithdrawStatuses((prev) => prev.map((s, j) => j === i ? { ...s, status: 'failed', error: 'No recipient address' } : s));
        continue;
      }

      setAutoWithdrawStatuses((prev) => prev.map((s, j) => j === i ? { ...s, status: 'withdrawing' } : s));

      try {
        const enrichedKey = await enrichKey(keys[i]);
        const isSolana = !!(enrichedKey.chain_name?.toLowerCase().includes('solana') || enrichedKey.solana_address);

        const isNative = !enrichedKey.token_address || enrichedKey.token_address === '0x0000000000000000000000000000000000000000';

        let relayResult;
        if (isSolana) {
          const denomBaseUnits = enrichedKey.denomination_wei
            ? BigInt(enrichedKey.denomination_wei)
            : BigInt(Math.floor(parseFloat(enrichedKey.denomination) * 1e9));
          const tenPercent = denomBaseUnits / 10n;
          const minCap = 10_000_000n;
          const maxFee = (tenPercent > minCap ? tenPercent : minCap).toString();

          relayResult = await executeSolanaRelayedWithdrawal({
            key: enrichedKey, recipientAddress: recipient, relayInfo,
            maxRelayerFee: maxFee, nodeUrl: relayInfo.nodeUrl,
            isSwap: false, targetAddress: null,
          });
        } else {
          const maxFee = estimateMaxRelayerFee(relayInfo, enrichedKey.denomination_wei, isNative);
          relayResult = await executeRelayedWithdrawal({
            key: enrichedKey, recipientAddress: recipient, relayInfo,
            maxRelayerFee: maxFee, nodeUrl: relayInfo.nodeUrl,
            isSwap: false, targetAddress: null,
          });
        }

        if (relayResult.success) {
          const wTxHash = relayResult.txHash;
          setAutoWithdrawStatuses((prev) => prev.map((s, j) => j === i ? { ...s, status: 'success', txHash: wTxHash } : s));

          // Mark key as used
          const keyIdx = enrichedKey.key_index ?? enrichedKey.keyIndex;
          const markUsed = (k) =>
            (k.key_index === keyIdx || k.keyIndex === keyIdx)
              ? { ...k, isUsed: true, withdrawalTxHash: wTxHash, withdrawalTimestamp: Date.now(), withdrawalRecipient: recipient }
              : k;
          setReconstructedKeys((prev) => prev.map(markUsed));

          // Persist to deposit tracker — use persisted keys (not stale React state)
          const depositTxHash = txHash;
          if (depositTxHash) {
            const persisted = depositTracker.getDepositByTxHash(depositTxHash);
            const existingKeys = persisted?.keys || [];
            const existingWithdrawals = persisted?.withdrawals || [];
            const newWithdrawal = {
              txHash: wTxHash, timestamp: Date.now(), recipient,
              keyIndex: keyIdx, chainName: enrichedKey.chain_name, tokenSymbol: enrichedKey.token_symbol,
              amount: enrichedKey.denomination, denomination: enrichedKey.denomination,
              chainType: isSolana ? 'solana' : 'evm',
            };
            depositTracker.updateDepositStatus(depositTxHash, 'keys_received', {
              keys: existingKeys.map(markUsed),
              withdrawals: [...existingWithdrawals, newWithdrawal],
            });
          }
        } else {
          throw new Error(relayResult.error || 'Relay withdrawal failed');
        }
      } catch (error) {
        console.error(`Auto-withdrawal failed for key ${i}:`, error);
        setAutoWithdrawStatuses((prev) => prev.map((s, j) => j === i ? { ...s, status: 'failed', error: error.message } : s));
      }
    }

    // Check if all succeeded
    setAutoWithdrawStatuses((prev) => {
      const allDone = prev.every((s) => s.status === 'success');
      if (allDone) {
        toast.success('All withdrawals completed successfully!');
        // Mark deposit as fully withdrawn
        if (txHash) depositTracker.updateDepositStatus(txHash, 'withdrawn');
      } else {
        const failedCount = prev.filter((s) => s.status === 'failed').length;
        toast.warn(`${failedCount} withdrawal(s) failed. Use the Unlock button to retry manually.`);
      }
      return prev;
    });

    autoWithdrawRunningRef.current = false;
  };

  // ---------- Manual Withdrawal (fallback) ----------

  // Enrich a raw key with both camelCase and snake_case properties + token config
  const enrichKey = async (key) => {
    const enriched = {
      ...key,
      keyIndex: key.key_index ?? key.keyIndex,
      key_index: key.key_index ?? key.keyIndex,
      tree_index: key.tree_index ?? key.treeIndex,
      merkle_root_id: key.merkle_root_id ?? key.merkleRootId,
      merkleRootId: key.merkle_root_id ?? key.merkleRootId,
      denomination: key.denomination,
      denomination_wei: key.denomination_wei,
      merkleRoot: key.merkle_root ?? key.merkleRoot,
      merkleProof: key.merkle_proof ?? key.merkleProof,
      merkle_root: key.merkle_root ?? key.merkleRoot,
      merkle_proof: key.merkle_proof ?? key.merkleProof,
      private_key: key.private_key,
      ethereum_address: key.ethereum_address,
      solana_address: key.solana_address,
      chain_name: key.chain_name || 'localhost',
      chain_id: key.chain_id || 31337,
      treasury_address: key.treasury_address,
      token_symbol: key.token_symbol || 'ETH',
      token_address: key.token_address || (key.chain_name?.includes('solana') ? '' : '0x0000000000000000000000000000000000000000'),
      token_decimals: key.token_decimals,
      isUsed: key.isUsed || false,
    };

    // Fetch missing token config from backend
    const sym = enriched.token_symbol;
    const needsAddr = !enriched.token_address || enriched.token_address === '0x0000000000000000000000000000000000000000';
    const needsDec = enriched.token_decimals === undefined;

    if ((needsAddr || needsDec) && sym !== 'ETH') {
      try {
        const tokens = await multiTokenTreasuryService.getSupportedTokens(enriched.chain_id);
        const tc = tokens?.find(t => t.symbol === sym);
        if (tc) {
          if (needsAddr) enriched.token_address = tc.address;
          if (needsDec) enriched.token_decimals = tc.decimals;
        }
      } catch (err) {
        console.warn('Failed to enhance key with token config:', err);
      }
    }
    if (needsDec && sym === 'ETH') enriched.token_decimals = 18;

    // Add display name for UI
    const chain = supportedChains.find(c => c.chain_name === enriched.chain_name);
    enriched.chainDisplayName = chain?.name || enriched.chain_name?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '';

    return enriched;
  };

  const openWithdrawDialog = async (keys, preSelectKeyIndex = null) => {
    const enrichedKeys = await Promise.all(keys.map(enrichKey));

    const fakeDeposit = {
      txHash: txHash || `uploaded-${Date.now()}`,
      amount: enrichedKeys.reduce((sum, k) => sum + parseFloat(k.denomination || 0), 0),
      keys: enrichedKeys,
      requestId: `dkg-${Date.now()}`,
    };
    setWithdrawDeposit(fakeDeposit);
    setAvailableKeys(enrichedKeys);
    setSelectedKeyIndex(preSelectKeyIndex ?? enrichedKeys[0]?.keyIndex ?? null);
    setRecipientAddress('');
    setWithdrawalMode('gasfree');
    setRelayJobId(null);
    setRelayNodeUrl(null);
    setSimpleWithdrawMode(false);
    setWithdrawDialog(true);
  };

  const [simpleWithdrawMode, setSimpleWithdrawMode] = useState(false);

  const openWithdrawForKey = async (key) => {
    // Simple mode: just set the key, default to relay, open simple dialog
    const enrichedKeys = await Promise.all([key].map(enrichKey));
    const enrichedKey = enrichedKeys[0];
    const keyIndex = enrichedKey.keyIndex ?? enrichedKey.key_index;

    const fakeDeposit = {
      txHash: txHash || `uploaded-${Date.now()}`,
      amount: parseFloat(enrichedKey.denomination || 0),
      keys: enrichedKeys,
      requestId: `dkg-${Date.now()}`,
    };
    setWithdrawDeposit(fakeDeposit);
    setAvailableKeys(enrichedKeys);
    setSelectedKeyIndex(keyIndex);
    setRecipientAddress('');
    setWithdrawalMode('gasfree');
    setRelayJobId(null);
    setRelayNodeUrl(null);
    setSimpleWithdrawMode(true);
    setWithdrawDialog(true);
  };

  const executeWithdrawal = async () => {
    if (!withdrawDeposit) {
      toast.error('Missing required data for withdrawal');
      return;
    }

    const selectedKey = availableKeys.find(key => key.keyIndex === selectedKeyIndex);
    const isSolanaWithdrawal = selectedKey && (
      selectedKey.chain_name?.toLowerCase().includes('solana') ||
      selectedKey.solana_address ||
      selectedKey.chain_type === 'solana'
    );
    const isTronWithdrawal = selectedKey && (
      selectedKey.chain_name?.toLowerCase().startsWith('tron') ||
      selectedKey.chain_type === 'tron'
    );
    // In simple mode, fall back to direct if relay is unavailable
    const isGasFree = (withdrawalMode === 'gasfree' || withdrawalMode === 'swap') && !!relayInfo && relayerHasBalance;

    // Validate wallet connection and address format
    if (isSolanaWithdrawal) {
      if (!isGasFree && (!solanaPublicKey || !solanaConnected || !solanaConnection)) {
        toast.error('Please connect your Phantom wallet for direct Solana withdrawals');
        return;
      }
      if (getChainType(recipientAddress) !== 'solana') {
        toast.error('Please enter a valid Solana address');
        return;
      }
      try {
        const { PublicKey } = await import('@solana/web3.js');
        new PublicKey(recipientAddress);
      } catch (e) {
        toast.error('Invalid Solana address format');
        return;
      }
    } else if (isTronWithdrawal) {
      // Tron withdrawals are relay-only (TronLink can't sign the wrapped tx).
      if (getChainType(recipientAddress) !== 'tron') {
        toast.error('Please enter a valid Tron address');
        return;
      }
    } else {
      if (!isGasFree && (!provider || !signer)) {
        toast.error('Please connect your MetaMask wallet for direct withdrawals');
        return;
      }
      if (!recipientAddress || !ethers.utils.isAddress(recipientAddress)) {
        toast.error('Please enter a valid EVM address');
        return;
      }
    }

    if (selectedKeyIndex === null) { toast.error('Please select a key to withdraw'); return; }
    if (!selectedKey) { toast.error('Selected key not found'); return; }

    try {
      setWithdrawing(true);

      // Pre-flight: check if key is already used on-chain before attempting any withdrawal
      try {
        if (isSolanaWithdrawal) {
          const conn = solanaConnection;
          if (conn && selectedKey.merkle_root && selectedKey.tree_index !== undefined) {
            const used = await solanaInternullService.isKeyUsedOnChain(
              conn, selectedKey.merkle_root, selectedKey.tree_index, selectedKey.merkle_root_id
            );
            if (used) {
              toast.error('This key has already been used on-chain. Please select a different key.');
              setWithdrawing(false);
              return;
            }
          }
        } else if (isTronWithdrawal) {
          // Tron: use TronGrid HTTP — skip ethers JSON-RPC.
          if (selectedKey.merkle_root_id !== undefined && selectedKey.tree_index !== undefined && selectedKey.treasury_address) {
            const { isTronKeyUsed, tronGridHostFor } = await import('../services/tronTreasuryService');
            const chainName = selectedKey.chain_name || 'tron-nile';
            const tronGridHost = tronGridHostFor(chainName, multiTokenTreasuryService.getRpcUrlByChainName(chainName));
            const used = await isTronKeyUsed({
              tronGridHost,
              contractAddress: selectedKey.treasury_address,
              merkleRootId: selectedKey.merkle_root_id,
              treeIndex: selectedKey.tree_index,
            });
            if (used) {
              toast.error('This key has already been used on-chain. Please select a different key.');
              setWithdrawing(false);
              return;
            }
          }
        } else {
          if (selectedKey.merkle_root_id !== undefined && selectedKey.tree_index !== undefined) {
            // Use a provider for the TARGET chain, not the user's MetaMask chain
            const targetChainId = selectedKey.chain_id || selectedKey.chainId;
            const checkProvider = targetChainId ? getProviderForChain(targetChainId) : provider;
            if (checkProvider) {
              const { checkMultipleKeys } = await import('../services/treasuryContractService');
              const results = await checkMultipleKeys(checkProvider, [{
                merkleRootId: selectedKey.merkle_root_id ?? selectedKey.merkleRootId,
                merkleRoot: selectedKey.merkle_root ?? selectedKey.merkleRoot,
                denomination: selectedKey.denomination,
                keyIndex: selectedKey.tree_index ?? selectedKey.treeIndex,
                treasury_address: selectedKey.treasury_address,
              }]);
              if (results[0]?.success && results[0]?.isUsed) {
                toast.error('This key has already been used on-chain. Please select a different key.');
                setWithdrawing(false);
                return;
              }
            }
          }
        }
      } catch (preCheckErr) {
        console.warn('Pre-flight key check failed (proceeding anyway):', preCheckErr.message);
      }

      let result;

      if (isGasFree || isTronWithdrawal) {
        const keyToUse = withdrawDeposit.keys.find(k => k.key_index === selectedKeyIndex);
        if (!keyToUse) throw new Error('Selected key not found');
        result = await executeGasFreeWithdrawal(keyToUse, isSolanaWithdrawal);
      } else if (isSolanaWithdrawal) {
        result = await executeDirectSolanaWithdrawal();
      } else {
        result = await executeDirectEvmWithdrawal(selectedKey);
      }

      if (result.success) {
        const withdrawalTxHash = result.receipt?.transactionHash || result.txHash;
        const markUsed = (k) =>
          (k.key_index === selectedKeyIndex || k.keyIndex === selectedKeyIndex)
            ? { ...k, isUsed: true, withdrawalTxHash, withdrawalTimestamp: Date.now(), withdrawalRecipient: recipientAddress }
            : k;

        // Mark key as used in reconstructedKeys (Phase 3 cards)
        setReconstructedKeys(prev => prev.map(markUsed));

        // Mark key as used in the dialog's availableKeys and auto-select next unused
        setAvailableKeys(prev => {
          const updated = prev.map(markUsed);
          const nextUnused = updated.find(k => !k.isUsed);
          if (nextUnused) {
            setSelectedKeyIndex(nextUnused.keyIndex);
          }
          return updated;
        });

        // Update withdrawDeposit.keys too (used by executeWithdrawal to find key_index)
        setWithdrawDeposit(prev => prev ? { ...prev, keys: prev.keys.map(markUsed) } : prev);

        // Persist to depositTracker so Dashboard stays in sync
        const depositTxHash = txHash || withdrawDeposit?.txHash;
        if (depositTxHash) {
          // Use persisted keys from deposit tracker (not stale React state) to avoid
          // losing isUsed flags from previous withdrawals in the same session
          const persistedDeposit = depositTracker.getDepositByTxHash(depositTxHash);
          const allKeys = persistedDeposit?.keys || reconstructedKeys || withdrawDeposit?.keys || [];
          const updatedKeys = allKeys.map(markUsed);
          const allUsed = updatedKeys.every(k => k.isUsed);
          const newWithdrawal = {
            txHash: withdrawalTxHash, timestamp: Date.now(), recipient: recipientAddress,
            keyIndex: selectedKeyIndex,
            chainName: selectedKey?.chain_name || 'Unknown',
            tokenSymbol: selectedKey?.token_symbol || 'ETH',
            amount: selectedKey?.denomination || '0',
            denomination: selectedKey?.denomination || '0',
            chainType: selectedKey?.chain_name?.toLowerCase().startsWith('tron') ? 'tron'
              : selectedKey?.chain_name?.includes('solana') ? 'solana' : 'evm',
          };
          // Read existing withdrawals from persistent storage (not stale React state)
          const existingWithdrawals = persistedDeposit?.withdrawals || withdrawDeposit?.withdrawals || [];

          // Deduplicate: skip if this txHash was already recorded (relay returned cached result)
          const isDuplicate = existingWithdrawals.some(w => w.txHash === withdrawalTxHash);
          if (isDuplicate) {
            console.warn('Duplicate txHash detected — relay likely returned a cached result:', withdrawalTxHash);
            toast.warn('This withdrawal was already recorded. The relay may have returned a cached result.');
            setWithdrawing(false);
            return;
          }

          // Ensure deposit exists in tracker first. Use the chain-appropriate
          // user address so Tron deposits stay under the Tron address (not the
          // EVM `account` when MetaMask is also connected).
          const recChainType = (selectedKey?.chain_name?.toLowerCase().startsWith('tron') || selectedKey?.chain_type === 'tron') ? 'tron'
            : (selectedKey?.chain_name?.toLowerCase().includes('solana') || selectedKey?.solana_address) ? 'solana'
            : 'evm';
          const userAddr = recChainType === 'tron'
            ? (tronAddress || withdrawDeposit?.userAddress || '')
            : recChainType === 'solana'
              ? (solanaPublicKey?.toString() || withdrawDeposit?.userAddress || '')
              : (account || withdrawDeposit?.userAddress || '');
          depositTracker.ensureDeposit(
            depositTxHash, userAddr,
            updatedKeys.reduce((sum, k) => sum + parseFloat(k.denomination || 0), 0),
            selectedKey?.chain_id || null,
            { token: selectedKey?.token_symbol || 'ETH', tokenAddress: selectedKey?.token_address || '' }
          );

          depositTracker.updateDepositStatus(depositTxHash, allUsed ? 'withdrawn' : 'keys_received', {
            keys: updatedKeys,
            withdrawals: [...existingWithdrawals, newWithdrawal],
          });
        }

        // Show success dialog with explorer link
        const withdrawChainName = selectedKey?.chain_name || 'sepolia';
        setSuccessDetails({
          title: 'Unlock Successful',
          message: `Successfully unlocked ${selectedKey?.denomination || ''} ${selectedKey?.token_symbol || 'ETH'} to ${recipientAddress.slice(0, 6)}...${recipientAddress.slice(-4)}`,
          txHash: withdrawalTxHash,
          explorerUrl: getExplorerUrl(withdrawalTxHash, withdrawChainName),
          chainName: getChainDisplayName(withdrawChainName),
        });
        setSuccessDialog(true);
      } else {
        throw new Error(result.error || 'Withdrawal failed');
      }
    } catch (error) {
      console.error('Withdrawal failed:', error);
      if (error.code === 4001 || error.code === 'ACTION_REJECTED' ||
          (error.message && (error.message.includes('user rejected') || error.message.includes('user denied')))) {
        toast.info('Transaction cancelled by user');
      } else {
        toast.error(error.message || 'Withdrawal failed');
      }
    } finally {
      setWithdrawing(false);
      setRelayJobId(null);
      setRelayNodeUrl(null);
    }
  };

  // Get a provider for the correct target chain (not the user's MetaMask chain)
  const getProviderForChain = (chainId) => {
    try {
      // First try RPC URL from backend chain config
      const rpcUrl = multiTokenTreasuryService.getRpcUrlForChain(chainId);
      if (rpcUrl) {
        return new ethers.providers.JsonRpcProvider(rpcUrl);
      }
      // Fallback to user's provider (may be wrong chain, but better than nothing)
      return provider;
    } catch (e) {
      return provider;
    }
  };

  // Verify an EVM relay tx actually succeeded on-chain
  const verifyEvmRelayTx = async (relayTxHash, chainId) => {
    try {
      // Use a provider for the TARGET chain, not the user's MetaMask chain
      const verifyProvider = getProviderForChain(chainId);
      if (!verifyProvider) return;

      const receipt = await verifyProvider.getTransactionReceipt(relayTxHash);

      // If receipt is null, the TX doesn't exist on this chain — suspicious
      if (!receipt) {
        console.warn('Relay TX receipt not found on target chain — may be cached/stale');
        // Check if this txHash was already recorded in our history
        const depositTx = txHash || withdrawDeposit?.txHash;
        if (depositTx) {
          const persisted = depositTracker.getDepositByTxHash(depositTx);
          const existing = persisted?.withdrawals || [];
          if (existing.some(w => w.txHash === relayTxHash)) {
            throw new Error('This withdrawal transaction was already recorded. The key may have already been used.');
          }
        }
        return; // Can't verify further, proceed cautiously
      }

      // Check if the TX reverted
      if (receipt.status === 0) {
        throw new Error('Relay transaction reverted on-chain. The key may have already been used.');
      }

      // Check if the TX is stale (older than 5 minutes) — relay returned a cached result
      if (receipt.blockNumber) {
        const block = await verifyProvider.getBlock(receipt.blockNumber);
        if (block && block.timestamp) {
          const txAgeSeconds = Math.floor(Date.now() / 1000) - block.timestamp;
          if (txAgeSeconds > 300) { // 5 minutes
            throw new Error(`Relay returned a stale transaction (${Math.floor(txAgeSeconds / 60)} min old). The key may have already been used.`);
          }
        }
      }
    } catch (e) {
      if (e.message.includes('reverted on-chain') || e.message.includes('already recorded') || e.message.includes('stale transaction')) throw e;
      console.warn('Could not verify relay tx on-chain:', e.message);
    }
  };

  // Verify a Solana relay tx actually succeeded on-chain
  const verifySolanaRelayTx = async (txHash) => {
    try {
      const conn = solanaConnection;
      if (!conn) return;

      const tx = await conn.getTransaction(txHash, { commitment: 'confirmed', maxSupportedTransactionVersion: 0 });
      if (tx && tx.meta && tx.meta.err) {
        throw new Error('Relay transaction failed on Solana. The key may have already been used. Please check the transaction on the explorer.');
      }
    } catch (e) {
      if (e.message.includes('failed on Solana')) throw e;
      console.warn('Could not verify Solana relay tx:', e.message);
    }
  };

  // Check if a relay-returned txHash is already in our records (cached relay result)
  const isRelayTxAlreadyRecorded = (relayTxHash) => {
    const depositTx = txHash || withdrawDeposit?.txHash;
    if (!depositTx || !relayTxHash) return false;
    const persisted = depositTracker.getDepositByTxHash(depositTx);
    return (persisted?.withdrawals || []).some(w => w.txHash === relayTxHash);
  };

  const executeGasFreeWithdrawal = async (keyToUse, isSolanaWithdrawal) => {
    if (!relayInfo) {
      throw new Error('Relayer information not available. Please try again.');
    }

    const onProgress = (progress) => {
      if (progress.jobId) {
        setRelayJobId(progress.jobId);
        setRelayNodeUrl(relayInfo.nodeUrl);
      }
    };

    if (isSolanaWithdrawal) {
      const denomBaseUnits = keyToUse.denomination_wei
        ? BigInt(keyToUse.denomination_wei)
        : BigInt(Math.floor(parseFloat(keyToUse.denomination) * 1e9));
      const tenPercent = denomBaseUnits / 10n;
      const minCap = 10_000_000n; // 0.01 SOL
      const maxFee = (tenPercent > minCap ? tenPercent : minCap).toString();

      const relayResult = await executeSolanaRelayedWithdrawal({
        key: keyToUse, recipientAddress, relayInfo, maxRelayerFee: maxFee,
        nodeUrl: relayInfo.nodeUrl, isSwap: withdrawalMode === 'swap', targetAddress: null,
      }, onProgress);

      if (relayResult.success) {
        // Check if relay returned a previously-seen txHash (cached result for used key)
        if (isRelayTxAlreadyRecorded(relayResult.txHash)) {
          throw new Error('Relay returned a previously recorded transaction. This key has likely already been used.');
        }
        // Verify the tx actually succeeded on-chain
        if (relayResult.txHash) await verifySolanaRelayTx(relayResult.txHash);
        return { success: true, txHash: relayResult.txHash, receipt: { transactionHash: relayResult.txHash } };
      }
      throw new Error(relayResult.error || 'Relayed withdrawal failed');
    } else {
      const isNative = !keyToUse.token_address || keyToUse.token_address === '0x0000000000000000000000000000000000000000';
      const maxFee = estimateMaxRelayerFee(relayInfo, keyToUse.denomination_wei, isNative);

      if (isNative) {
        let currentGasPrice = null;
        try { if (provider) currentGasPrice = await provider.getGasPrice(); } catch (e) { /* ignore */ }
        const denomBN = ethers.BigNumber.from(keyToUse.denomination_wei);
        const realisticFee = ethers.BigNumber.from(estimateRealisticRelayFee(relayInfo, keyToUse.denomination_wei, true, currentGasPrice));
        if (realisticFee.gte(denomBN)) {
          throw new Error(`Denomination too small for gas-free relay. Estimated fee exceeds key value. Please use Direct Withdrawal instead.`);
        }
      }

      const relayResult = await executeRelayedWithdrawal({
        key: keyToUse, recipientAddress, relayInfo, maxRelayerFee: maxFee,
        nodeUrl: relayInfo.nodeUrl, isSwap: withdrawalMode === 'swap', targetAddress: null,
      }, onProgress);

      if (relayResult.success) {
        // Check if relay returned a previously-seen txHash (cached result for used key)
        if (isRelayTxAlreadyRecorded(relayResult.txHash)) {
          throw new Error('Relay returned a previously recorded transaction. This key has likely already been used.');
        }
        // Verify the tx actually succeeded on-chain
        if (relayResult.txHash) await verifyEvmRelayTx(relayResult.txHash, keyToUse.chain_id);
        return { success: true, txHash: relayResult.txHash, receipt: { transactionHash: relayResult.txHash } };
      }
      throw new Error(relayResult.error || 'Relayed withdrawal failed');
    }
  };

  const executeDirectSolanaWithdrawal = async () => {
    const keyToUse = withdrawDeposit.keys.find(k => k.key_index === selectedKeyIndex);
    if (!keyToUse) throw new Error('Selected Solana key not found');

    // Use backend RPC for better performance, fall back to default connection
    let connectionToUse = solanaConnection;
    const backendRpcUrl = multiTokenTreasuryService.getRpcUrlByChainName(keyToUse.chain_name || 'solana-devnet');
    if (backendRpcUrl) {
      console.log('Using backend RPC for Solana withdrawal:', backendRpcUrl);
      connectionToUse = new Connection(backendRpcUrl, 'confirmed');
    }

    const result = await solanaInternullService.executeWithdrawal(keyToUse, recipientAddress, connectionToUse, solanaWallet);
    return result;
  };

  const executeDirectEvmWithdrawal = async (_selectedKey) => {
    const keyToUse = withdrawDeposit.keys.find(k => k.key_index === selectedKeyIndex);
    if (!keyToUse) throw new Error('Selected key not found');

    const keyChainId = keyToUse.chain_id;
    const keyChainConfig = formattedChains.find(c => c.chainId === keyChainId);
    if (!keyChainConfig) {
      throw new Error(`Chain configuration not found for ${keyToUse.chain_name} (ID: ${keyChainId})`);
    }

    const metamaskProvider = getMetaMaskProvider();
    if (!metamaskProvider) {
      throw new Error('MetaMask not found. Please install MetaMask for EVM withdrawals.');
    }

    // Check current network and switch if needed
    const currentChainIdHex = await metamaskProvider.request({ method: 'eth_chainId' });
    const currentChainId = parseInt(currentChainIdHex, 16);

    if (currentChainId !== keyChainId) {
      toast.info(`Please switch MetaMask to ${keyChainConfig.name} to continue withdrawal`);
      try {
        await metamaskProvider.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: `0x${keyChainId.toString(16)}` }],
        });
        await new Promise(resolve => setTimeout(resolve, 2000));
        toast.success(`Switched to ${keyChainConfig.name}`);
      } catch (switchError) {
        if (switchError.code === 4902) {
          const CHAIN_PARAMS = {
            11155111: { chainName: 'Sepolia Testnet', nativeCurrency: { name: 'SepoliaETH', symbol: 'ETH', decimals: 18 }, rpcUrls: [process.env.REACT_APP_RPC_URL || 'https://eth-sepolia.public.blastapi.io'], blockExplorerUrls: ['https://sepolia.etherscan.io'] },
            84532: { chainName: 'Base Sepolia', nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 }, rpcUrls: ['https://sepolia.base.org'], blockExplorerUrls: ['https://sepolia.basescan.org'] },
            80002: { chainName: 'Polygon Amoy Testnet', nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 }, rpcUrls: ['https://rpc-amoy.polygon.technology'], blockExplorerUrls: ['https://amoy.polygonscan.com'] },
            97: { chainName: 'BNB Smart Chain Testnet', nativeCurrency: { name: 'Test BNB', symbol: 'tBNB', decimals: 18 }, rpcUrls: ['https://data-seed-prebsc-1-s1.bnbchain.org:8545'], blockExplorerUrls: ['https://testnet.bscscan.com'] },
            998: { chainName: 'Hyperliquid Testnet', nativeCurrency: { name: 'USD', symbol: 'USD', decimals: 6 }, rpcUrls: ['https://api.hyperliquid-testnet.xyz/evm'], blockExplorerUrls: ['https://explorer.hyperliquid-testnet.xyz'] },
          };
          const params = CHAIN_PARAMS[keyChainId];
          if (params) {
            toast.info(`Adding ${params.chainName} to your wallet...`);
            await metamaskProvider.request({
              method: 'wallet_addEthereumChain',
              params: [{ chainId: `0x${keyChainId.toString(16)}`, ...params }],
            });
            await new Promise(resolve => setTimeout(resolve, 1000));
            toast.success(`Added and switched to ${params.chainName}`);
          } else {
            toast.error(`Please add chain ${keyChainId} to MetaMask manually`);
            throw new Error(`Chain ${keyChainConfig.name} not configured in MetaMask`);
          }
        } else {
          throw switchError;
        }
      }
    }

    // Create fresh provider from MetaMask
    const withdrawalProvider = new ethers.providers.Web3Provider(metamaskProvider);
    const finalChainId = (await withdrawalProvider.getNetwork()).chainId;

    if (finalChainId !== keyChainId) {
      throw new Error(`Network mismatch! Expected ${keyChainId}, but provider is on ${finalChainId}`);
    }

    await singleNodeWithdrawal.initialize(
      withdrawalProvider.provider || withdrawalProvider,
      keyToUse.treasury_address
    );

    return await singleNodeWithdrawal.executeWithdrawalWithKey(keyToUse, recipientAddress);
  };

  const handleDownloadKeys = () => {
    setPasswordDialogMode('download');
    setPasswordDialog(true);
  };

  const downloadKeysWithPassword = async (password) => {
    try {
      // Enrich keys with camelCase properties before downloading
      const enrichedKeys = await Promise.all(reconstructedKeys.map(enrichKey));

      const fakeDeposit = {
        txHash: txHash || `easy-${Date.now()}`,
        requestId: `dkg-${Date.now()}`,
      };

      const { keysData, filename } = prepareKeysForDownload(enrichedKeys, fakeDeposit, null);
      const encrypted = KeyEncryptionService.encryptKeys(keysData, password);
      KeyEncryptionService.downloadEncryptedKeys(encrypted, filename);
      setPasswordDialog(false);
      toast.success('Keys downloaded and encrypted!');
    } catch (err) {
      toast.error('Failed to encrypt keys: ' + err.message);
    }
  };

  // ---------- Upload Keys ----------

  const handleUploadKeys = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const encryptedData = await KeyEncryptionService.readEncryptedFile(file);
      encryptedDataRef.current = encryptedData;
      setPasswordDialogMode('upload');
      setPasswordDialog(true);
    } catch (error) {
      console.error('File read error:', error);
      toast.error('Failed to read file: ' + error.message);
    }
    event.target.value = '';
  };

  const uploadKeysWithPassword = async (password) => {
    try {
      const encryptedData = encryptedDataRef.current;
      encryptedDataRef.current = null;

      let keysData = KeyEncryptionService.decryptKeys(encryptedData, password);
      keysData = processUploadedKeys(keysData);

      const uploadedKeys = keysData.availableKeys || keysData.keys || [];
      if (uploadedKeys.length === 0) {
        throw new Error('No keys found in file');
      }

      // Load uploaded keys into reconstructedKeys state and jump to Phase 3
      setReconstructedKeys(uploadedKeys);
      setSelectedToken(uploadedKeys[0]?.token_symbol || uploadedKeys[0]?.tokenSymbol || '');
      if (keysData.depositId) setTxHash(keysData.depositId);
      setPhase(3);
      setPasswordDialog(false);

      toast.success(`Loaded ${uploadedKeys.length} key${uploadedKeys.length > 1 ? 's' : ''} from backup!`);

      // Open withdrawal dialog with all keys
      await openWithdrawDialog(uploadedKeys);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to load keys: ' + error.message);
      throw error;
    }
  };

  const resetFlow = () => {
    setPhase(0);
    setSelectedToken('');
    setKeyRows([{ chain: '', denomination: '', recipientAddress: '' }]);
    setDepositChainId('');
    setTxHash('');
    setConfirmations(0);
    setConfirmationStatus('waiting');
    setKeyRequestProgress(null);
    setConfirmError(null);
    setReconstructedKeys([]);
    setAutoWithdrawStatuses(null);
    autoWithdrawRunningRef.current = false;
  };

  // ---------- Explorer URL Helper ----------

  const getExplorerUrl = (hash, chainNameOrId) => {
    if (!hash) return '';
    // Prefer backend-supplied block_explorer (handles Tron, Solana, EVM).
    if (chainNameOrId && typeof chainNameOrId === 'string') {
      return buildExplorerTxUrl(chainNameOrId, hash, supportedChains);
    }
    // Fallback: look up chain by depositChainId.
    if (depositChainId) {
      const chain = supportedChains.find(c => c.chain_id === depositChainId || c.chainId === depositChainId);
      if (chain) return buildExplorerTxUrl(chain, hash, supportedChains);
    }
    if (isSolanaDeposit) {
      return `https://explorer.solana.com/tx/${hash}?cluster=devnet`;
    }
    if (isTronDeposit) {
      return `https://nile.tronscan.org/#/transaction/${hash}`;
    }
    const evmExplorers = {
      11155111: 'https://sepolia.etherscan.io',
      97: 'https://testnet.bscscan.com',
      56: 'https://bscscan.com',
      80002: 'https://amoy.polygonscan.com',
      137: 'https://polygonscan.com',
      84532: 'https://sepolia.basescan.org',
      8453: 'https://basescan.org',
      998: 'https://explorer.hyperliquid-testnet.xyz',
      999: 'https://explorer.hyperliquid.xyz',
      1: 'https://etherscan.io',
    };
    const base = evmExplorers[depositChainId] || 'https://etherscan.io';
    return `${base}/tx/${hash}`;
  };

  const getChainDisplayName = (chainName) => {
    if (!chainName) return '';
    const shortNames = {
      'sepolia': 'Ethereum',
      'polygon-amoy': 'Polygon',
      'bsc-testnet': 'BNB Chain',
      'base-sepolia': 'Base',
      'hyperliquid': 'Hyperliquid',
      'solana-devnet': 'Solana',
    };
    if (shortNames[chainName]) return shortNames[chainName];
    const chain = supportedChains.find(c => c.chain_name === chainName);
    const raw = chain?.name || chainName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    return raw.replace(/\s*(Sepolia|Testnet|Devnet|Amoy|Goerli)\s*/gi, '').trim() || raw;
  };

  const blockTime = BLOCK_TIMES[depositChainId] || 12;
  const estimatedWaitSeconds = Math.max(0, (REQUIRED_CONFIRMATIONS - confirmations) * blockTime);

  // ---------- Render ----------

  return (
    <Box sx={{ maxWidth: phase === 0 ? 960 : 680, mt: { xs: 1, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            Lock Funds
          </Typography>
          <Typography variant="body2" sx={{ color: subtleText }}>
            Configure secure multi-chain locking with auto-withdrawal.
          </Typography>
        </Box>
        <Button
          size="small"
          startIcon={<Upload />}
          onClick={handleUploadKeys}
          sx={{ color: subtleText, fontSize: '0.8rem', textTransform: 'none' }}
        >
          Upload saved keys
        </Button>
      </Box>

      {/* Stepper */}
      <Stepper activeStep={phase} sx={{ mb: 4 }} alternativeLabel={!isMobile}>
        {STEPS.map((label, index) => (
          <Step key={label} completed={phase > index}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Phase 0: Configure — 2-column layout on desktop */}
      {phase === 0 && (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 340px' },
          gap: 3,
          alignItems: 'start',
        }}>
          {/* Left: Form */}
          <Card
            variant="outlined"
            sx={{ border: '1px solid', borderColor, bgcolor: cardBg }}
          >
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <KeyConfigForm
                selectedToken={selectedToken}
                onTokenChange={handleTokenChange}
                keyRows={keyRows}
                onUpdateRow={handleUpdateRow}
                onAddRow={handleAddRow}
                onRemoveRow={handleRemoveRow}
                denomsByChain={denomsByChain}
                allTokenSymbols={allTokenSymbols}
                tokenLocked={tokenLocked}
                chainsForToken={chainsForToken}
                isDark={isDark}
                isMobile={isMobile}
              />

              {/* Alerts */}
              {selectedToken && !connected && !solanaConnected && !tronConnected && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Please connect your wallet using the button in the header before continuing.
                </Alert>
              )}
              {!dkgV2Ready && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  DKG nodes are not ready. Key generation may fail. Please try again later.
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Right: Summary Panel */}
          <Box sx={{ position: { md: 'sticky' }, top: { md: 80 } }}>
            <Card
              variant="outlined"
              sx={{
                border: '1px solid',
                borderColor,
                bgcolor: isDark ? '#141416' : '#fff',
                boxShadow: isDark ? '0 32px 64px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.08)',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block', mb: 3 }}>
                  Summary
                </Typography>

                {/* Lock Amount */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2 }}>
                  <Typography variant="body2" sx={{ color: subtleText }}>Lock Amount</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                    {totalAmount > 0 ? `${totalAmount} ${selectedToken || '—'}` : '—'}
                  </Typography>
                </Box>

                {/* Key count */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2 }}>
                  <Typography variant="body2" sx={{ color: subtleText }}>Keys</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {keyRows.filter(r => r.chain && r.denomination).length} configured
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Total Deposit Required */}
                <Box
                  sx={{
                    p: 2,
                    mb: 3,
                    bgcolor: isDark ? 'rgba(34,197,94,0.05)' : 'rgba(34,197,94,0.08)',
                    border: '1px solid',
                    borderColor: totalAmount > 0 ? (isDark ? 'rgba(34,197,94,0.3)' : 'rgba(34,197,94,0.4)') : borderColor,
                    position: 'relative',
                  }}
                >
                  <Typography variant="caption" sx={{
                    position: 'absolute',
                    top: -10,
                    left: 12,
                    px: 1,
                    bgcolor: isDark ? '#141416' : '#fff',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    fontSize: '0.6rem',
                    color: '#22c55e',
                  }}>
                    Total Deposit Required
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, fontFamily: 'monospace', color: '#22c55e' }}>
                    {totalAmount > 0 ? `${totalAmount} ${selectedToken}` : '—'}
                  </Typography>
                </Box>

                {/* Deposit On */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" sx={{ color: subtleText, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: '0.6rem', display: 'block', mb: 1 }}>
                    Deposit On
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={depositChainId}
                      onChange={(e) => setDepositChainId(e.target.value)}
                      displayEmpty
                      renderValue={(v) => {
                        if (!v) return <Typography sx={{ color: subtleText, fontSize: '0.85rem' }}>Select chain...</Typography>;
                        const chain = depositChainOptions.find(c => c.chainId === v);
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getChainLogoUrl(chain?.chain_name || chain?.name) && (
                              <Box component="img" src={getChainLogoUrl(chain?.chain_name || chain?.name)} alt={chain?.name} sx={{ width: 18, height: 18, borderRadius: '50%' }} />
                            )}
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{chain?.name || v}</Typography>
                          </Box>
                        );
                      }}
                    >
                      {depositChainOptions.map((chain) => (
                        <MenuItem key={chain.chainId} value={chain.chainId}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getChainLogoUrl(chain.chain_name || chain.name) && (
                              <Box component="img" src={getChainLogoUrl(chain.chain_name || chain.name)} alt={chain.name} sx={{ width: 18, height: 18, borderRadius: '50%' }} />
                            )}
                            {chain.name}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Confirm Button */}
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  endIcon={<ArrowForward />}
                  disabled={!canContinueToDeposit}
                  onClick={() => setPhase(1)}
                  sx={{
                    py: 1.5,
                    background: canContinueToDeposit
                      ? 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)'
                      : undefined,
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    '&:hover': canContinueToDeposit ? {
                      background: 'linear-gradient(90deg, #16a34a 0%, #22c55e 100%)',
                      boxShadow: '0 4px 20px rgba(34,197,94,0.3)',
                    } : {},
                  }}
                >
                  Confirm Setup
                </Button>
              </CardContent>
            </Card>

            {/* Privacy Info Card */}
            <Card
              variant="outlined"
              sx={{
                mt: 2,
                border: '1px solid',
                borderColor,
                borderLeft: `3px solid ${isDark ? '#7c3aed' : '#8b5cf6'}`,
                bgcolor: isDark ? '#111318' : '#f8f9fa',
              }}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CheckCircle sx={{ fontSize: 16, color: isDark ? '#a78bfa' : '#7c3aed' }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: isDark ? '#a78bfa' : '#7c3aed' }}>
                    How Privacy Works
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: subtleText, lineHeight: 1.5, display: 'block' }}>
                  Withdrawal keys are generated via 3-of-5 distributed nodes — no single server sees your full private key. Relay withdrawals break the on-chain link between your deposit and recipient addresses.
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Phase 1: Deposit Confirmation */}
      {phase === 1 && (
        <Card
          variant="outlined"
          sx={{ border: '1px solid', borderColor, bgcolor: cardBg }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Lock {totalAmount} {selectedToken} on {depositChainInfo?.name}
            </Typography>

            <Box sx={{ bgcolor: subtleBg, p: 2, borderRadius: 1, mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Summary</Typography>
              {keyRows
                .filter((r) => r.chain && r.denomination)
                .map((r, i) => {
                  const chainInfo = formattedChains.find((c) => c.chain_name === r.chain);
                  return (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      {getChainLogoUrl(r.chain) && (
                        <Box
                          component="img"
                          src={getChainLogoUrl(r.chain)}
                          alt={r.chain}
                          sx={{ width: 16, height: 16, borderRadius: '50%' }}
                        />
                      )}
                      <Box>
                        <Typography variant="body2">
                          {r.denomination} {selectedToken} on {chainInfo?.name || r.chain}
                        </Typography>
                        {r.recipientAddress && (
                          <Typography variant="caption" sx={{ color: subtleText, fontFamily: '"JetBrains Mono", monospace', fontSize: '0.7rem' }}>
                            → {r.recipientAddress.slice(0, 8)}...{r.recipientAddress.slice(-4)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2">Total</Typography>
                <Typography variant="subtitle2" sx={{ fontFamily: '"JetBrains Mono", monospace', color: '#22c55e' }}>
                  {totalAmount} {selectedToken}
                </Typography>
              </Box>
            </Box>

            {/* Connected wallet info */}
            {(connected || solanaConnected || tronConnected) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, p: 1.5, borderRadius: 1, bgcolor: subtleBg }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#22c55e' }} />
                <Typography variant="body2" sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.85rem' }}>
                  {isSolanaDeposit
                    ? solanaPublicKey?.toString()?.slice(0, 8) + '...' + solanaPublicKey?.toString()?.slice(-4)
                    : account?.slice(0, 8) + '...' + account?.slice(-4)
                  }
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Button
                startIcon={<ArrowBack />}
                onClick={() => setPhase(0)}
                disabled={depositLoading}
              >
                Back
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={executeDeposit}
                disabled={depositLoading || (!connected && !solanaConnected && !tronConnected)}
                startIcon={depositLoading ? <CircularProgress size={18} color="inherit" /> : undefined}
                sx={{
                  background: 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)',
                  px: 4,
                  '&:hover': { background: 'linear-gradient(90deg, #16a34a 0%, #22c55e 100%)' },
                }}
              >
                {depositLoading ? 'Locking...' : `Lock ${totalAmount} ${selectedToken}`}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Phase 2: Confirming + Key Request */}
      {phase === 2 && (
        <Card
          variant="outlined"
          sx={{ border: '1px solid', borderColor, bgcolor: cardBg }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            {/* Block confirmations */}
            {confirmationStatus === 'waiting' && (
              <>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Waiting for confirmations...
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: subtleText }}>
                      Block confirmations
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: '"JetBrains Mono", monospace' }}>
                      {confirmations}/{REQUIRED_CONFIRMATIONS}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(confirmations / REQUIRED_CONFIRMATIONS) * 100}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)',
                        borderRadius: 4,
                      },
                    }}
                  />
                  <Typography variant="caption" sx={{ color: subtleText, mt: 1, display: 'block' }}>
                    ~{estimatedWaitSeconds}s remaining ({depositChainInfo?.name}, {blockTime}s/block)
                  </Typography>
                </Box>
              </>
            )}

            {/* Confirmed → requesting keys */}
            {(confirmationStatus === 'confirmed' || confirmationStatus === 'signing' || confirmationStatus === 'requesting_keys') && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CheckCircle sx={{ color: '#22c55e' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Deposit confirmed!
                  </Typography>
                </Box>

                {confirmationStatus === 'signing' && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Please sign the message in your wallet to authenticate the key request.
                  </Alert>
                )}

                {confirmationStatus === 'requesting_keys' && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Generating keys...
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={18} />
                      <Typography variant="body2" sx={{ color: subtleText }}>
                        {keyRequestProgress?.message || 'Collecting keyshares from DKG nodes...'}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </>
            )}

            {/* Error state */}
            {confirmationStatus === 'error' && (
              <>
                <Alert severity="error" sx={{ mb: 2 }}>
                  {confirmError || 'Key generation failed.'}
                </Alert>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={retryKeyRequest}
                  sx={{ mb: 2 }}
                >
                  Retry Key Request
                </Button>
              </>
            )}

            {/* TX info */}
            {txHash && (
              <Box sx={{ p: 2, borderRadius: 1, bgcolor: subtleBg, mt: 2 }}>
                <Typography variant="caption" sx={{ color: subtleText }}>Transaction</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8rem' }}
                  >
                    {txHash.slice(0, 16)}...{txHash.slice(-8)}
                  </Typography>
                  <Button
                    size="small"
                    endIcon={<Launch sx={{ fontSize: 14 }} />}
                    href={getExplorerUrl(txHash)}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontSize: '0.75rem', minWidth: 'auto', p: 0.5 }}
                  >
                    View
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Phase 3: Withdrawing */}
      {phase === 3 && (
        <Card
          variant="outlined"
          sx={{ border: '1px solid', borderColor, bgcolor: cardBg }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              {autoWithdrawStatuses && autoWithdrawStatuses.every((s) => s.status === 'success') ? (
                <CheckCircle sx={{ color: '#22c55e', fontSize: 28 }} />
              ) : autoWithdrawStatuses && autoWithdrawStatuses.some((s) => s.status === 'withdrawing') ? (
                <CircularProgress size={24} sx={{ color: '#22c55e' }} />
              ) : (
                <CheckCircle sx={{ color: '#22c55e', fontSize: 28 }} />
              )}
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {autoWithdrawStatuses
                  ? autoWithdrawStatuses.every((s) => s.status === 'success')
                    ? 'All withdrawals complete!'
                    : autoWithdrawStatuses.some((s) => s.status === 'withdrawing' || s.status === 'pending')
                      ? 'Withdrawing via relay...'
                      : 'Withdrawals finished'
                  : 'Your keys are ready!'
                }
              </Typography>
            </Box>

            {/* Key cards with auto-withdrawal status */}
            {reconstructedKeys.map((key, index) => {
              const chainName = key.chain_name || 'unknown';
              const chainInfo = formattedChains.find((c) => c.chain_name === chainName);
              const autoStatus = autoWithdrawStatuses?.[index];
              const used = key.isUsed || autoStatus?.status === 'success';
              const isWithdrawing = autoStatus?.status === 'withdrawing';
              const isFailed = autoStatus?.status === 'failed';
              const isPending = autoStatus?.status === 'pending';

              return (
                <Card
                  key={index}
                  variant="outlined"
                  sx={{
                    mb: 1.5,
                    border: '1px solid',
                    borderColor: isFailed
                      ? (isDark ? 'rgba(239, 68, 68, 0.3)' : 'rgba(239, 68, 68, 0.4)')
                      : used
                        ? (isDark ? 'rgba(163,163,163,0.2)' : 'rgba(163,163,163,0.3)')
                        : isWithdrawing
                          ? (isDark ? 'rgba(234, 179, 8, 0.3)' : 'rgba(234, 179, 8, 0.4)')
                          : (isDark ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.3)'),
                    bgcolor: isFailed
                      ? (isDark ? 'rgba(239, 68, 68, 0.05)' : 'rgba(239, 68, 68, 0.05)')
                      : used
                        ? (isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)')
                        : (isDark ? 'rgba(34, 197, 94, 0.03)' : 'rgba(34, 197, 94, 0.05)'),
                    opacity: used && !isWithdrawing ? 0.7 : 1,
                  }}
                >
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {getChainLogoUrl(chainName) && (
                          <Box
                            component="img"
                            src={getChainLogoUrl(chainName)}
                            alt={chainName}
                            sx={{ width: 24, height: 24, borderRadius: '50%' }}
                          />
                        )}
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, textDecoration: used ? 'line-through' : 'none' }}>
                            {key.denomination} {key.token_symbol || selectedToken}
                          </Typography>
                          <Typography variant="caption" sx={{ color: subtleText }}>
                            on {chainInfo?.name || chainName}
                            {autoStatus?.recipient && (
                              <> → <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.7rem' }}>
                                {autoStatus.recipient.slice(0, 6)}...{autoStatus.recipient.slice(-4)}
                              </span></>
                            )}
                          </Typography>
                          {autoStatus?.txHash && (
                            <Box sx={{ mt: 0.25 }}>
                              <Button
                                size="small"
                                endIcon={<Launch sx={{ fontSize: 12 }} />}
                                href={getExplorerUrl(autoStatus.txHash, chainName)}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ fontSize: '0.65rem', minWidth: 'auto', p: 0, textTransform: 'none', color: '#22c55e' }}
                              >
                                View tx
                              </Button>
                            </Box>
                          )}
                          {isFailed && (
                            <Typography variant="caption" sx={{ color: '#ef4444', display: 'block', mt: 0.25 }}>
                              {autoStatus.error}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isWithdrawing && <CircularProgress size={18} sx={{ color: '#eab308' }} />}
                        {isPending && <Typography variant="caption" sx={{ color: subtleText }}>Queued</Typography>}
                        {used && !isWithdrawing && (
                          <CheckCircle sx={{ color: '#22c55e', fontSize: 20 }} />
                        )}
                        {isFailed && (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => openWithdrawForKey(key)}
                            sx={{
                              background: 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)',
                              '&:hover': { background: 'linear-gradient(90deg, #d97706 0%, #f59e0b 100%)' },
                            }}
                          >
                            Unlock
                          </Button>
                        )}
                        {!autoWithdrawStatuses && !used && (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => openWithdrawForKey(key)}
                            sx={{
                              background: 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)',
                              '&:hover': { background: 'linear-gradient(90deg, #16a34a 0%, #22c55e 100%)' },
                            }}
                          >
                            Unlock
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}

            <Divider sx={{ my: 2 }} />

            {/* Actions */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleDownloadKeys}
                sx={{ borderColor, color: subtleText, '&:hover': { borderColor: isDark ? '#404040' : '#d4d4d4' } }}
              >
                Download Backup
              </Button>
              <Button
                variant="outlined"
                startIcon={<Upload />}
                onClick={handleUploadKeys}
                sx={{ borderColor, color: subtleText, '&:hover': { borderColor: isDark ? '#404040' : '#d4d4d4' } }}
              >
                Upload Keys
              </Button>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={resetFlow}
                sx={{ borderColor, color: subtleText, '&:hover': { borderColor: isDark ? '#404040' : '#d4d4d4' } }}
              >
                Start New
              </Button>
              <Button
                variant="outlined"
                endIcon={<ArrowForward />}
                onClick={() => navigate('/dashboard')}
                sx={{ borderColor, color: subtleText, '&:hover': { borderColor: isDark ? '#404040' : '#d4d4d4' } }}
              >
                Go to Dashboard
              </Button>
            </Box>

            {!autoWithdrawStatuses && (
              <Alert severity="info" sx={{ mt: 3 }}>
                Your keys are stored locally in your browser. Download an encrypted backup to avoid losing them.
              </Alert>
            )}
            {autoWithdrawStatuses?.some((s) => s.status === 'failed') && (
              <Alert severity="warning" sx={{ mt: 3 }}>
                Some withdrawals failed. Click "Unlock" on the failed keys to retry manually, or download a backup for later.
              </Alert>
            )}
            {autoWithdrawStatuses?.every((s) => s.status === 'success') && (
              <Alert severity="success" sx={{ mt: 3 }}>
                All funds have been privately withdrawn to the specified addresses.
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Simple Withdrawal Dialog (Easy Mode — single key, auto-relay) */}
      {simpleWithdrawMode && (
        <SimpleWithdrawalDialog
          open={withdrawDialog}
          onClose={() => { setWithdrawDialog(false); setRelayJobId(null); setRelayNodeUrl(null); setSimpleWithdrawMode(false); }}
          selectedKey={availableKeys.find(k => k.keyIndex === selectedKeyIndex) || availableKeys[0]}
          recipientAddress={recipientAddress}
          onRecipientChange={setRecipientAddress}
          onExecuteWithdrawal={executeWithdrawal}
          onSwitchToDirect={() => {
            setSimpleWithdrawMode(false);
            setWithdrawalMode('gasfree');
          }}
          withdrawing={withdrawing}
          relayInfo={relayInfo}
          relayerHasBalance={relayerHasBalance}
          relayJobId={relayJobId}
          relayNodeUrl={relayNodeUrl}
          isDark={isDark}
        />
      )}

      {/* Full Withdrawal Dialog (for uploaded keys with multiple keys) */}
      {!simpleWithdrawMode && (
        <WithdrawalDialog
          open={withdrawDialog}
          onClose={() => { setWithdrawDialog(false); setRelayJobId(null); setRelayNodeUrl(null); }}
          withdrawDeposit={withdrawDeposit}
          availableKeys={availableKeys}
          selectedKeyIndex={selectedKeyIndex}
          onSelectKey={setSelectedKeyIndex}
          recipientAddress={recipientAddress}
          onRecipientChange={setRecipientAddress}
          withdrawalMode={withdrawalMode}
          onWithdrawalModeChange={setWithdrawalMode}
          relayInfo={relayInfo}
          relayerHasBalance={relayerHasBalance}
          withdrawing={withdrawing}
          onExecuteWithdrawal={executeWithdrawal}
          relayJobId={relayJobId}
          relayNodeUrl={relayNodeUrl}
          expandedChains={expandedChains}
          expandedTokens={expandedTokens}
          onToggleChain={(chain) => setExpandedChains((prev) => ({ ...prev, [chain]: !prev[chain] }))}
          onToggleToken={(chain, token) => setExpandedTokens((prev) => ({ ...prev, [`${chain}-${token}`]: !prev[`${chain}-${token}`] }))}
          onDownloadKeys={handleDownloadKeys}
          onUploadKeys={handleUploadKeys}
          isDark={isDark}
          isMobile={isMobile}
          account={account}
          solanaPublicKey={solanaPublicKey}
          solanaConnected={solanaConnected}
        />
      )}

      {/* Password Dialog for key download/upload */}
      <PasswordDialog
        open={passwordDialog}
        onClose={() => { setPasswordDialog(false); encryptedDataRef.current = null; }}
        onConfirm={passwordDialogMode === 'download' ? downloadKeysWithPassword : uploadKeysWithPassword}
        title={passwordDialogMode === 'download' ? 'Encrypt Keys for Backup' : 'Decrypt Key Backup'}
        description={passwordDialogMode === 'download'
          ? 'Enter a password to encrypt your keys. You will need this password to restore them later.'
          : 'Enter the password you used when saving these keys.'
        }
        confirmButtonText={passwordDialogMode === 'download' ? 'Download' : 'Load Keys'}
      />

      {/* Withdrawal Success Dialog */}
      <SuccessDialog
        open={successDialog}
        onClose={() => { setSuccessDialog(false); setWithdrawDialog(false); setSimpleWithdrawMode(false); setRelayJobId(null); setRelayNodeUrl(null); }}
        successDetails={successDetails}
        isDark={isDark}
        isMobile={isMobile}
      />

      {/* Hidden file input for uploading keys */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept=".enc,.json"
        style={{ display: 'none' }}
      />

      {/* Privacy notice + Pro mode link */}
      <Alert severity="info" variant="outlined" sx={{ mt: 4, mb: 2, borderColor: isDark ? 'rgba(59,130,246,0.3)' : undefined }}>
        <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.6 }}>
          <strong>Privacy note:</strong> Quick Privacy withdraws automatically after deposit, which reduces the anonymity set
          since withdrawals happen shortly after deposits. Splitting into multiple keys helps, but for maximum privacy use{' '}
          <Button
            size="small"
            onClick={() => navigate('/dashboard')}
            sx={{ fontSize: 'inherit', textTransform: 'none', p: 0, minWidth: 'auto', verticalAlign: 'baseline', lineHeight: 'inherit' }}
          >
            Pro Mode
          </Button>
          {' '}— it lets you save keys and withdraw at a later time, from a different wallet or device.{' '}
          <Button
            size="small"
            onClick={() => navigate('/guide')}
            sx={{ fontSize: 'inherit', textTransform: 'none', p: 0, minWidth: 'auto', verticalAlign: 'baseline', lineHeight: 'inherit' }}
          >
            Learn more
          </Button>
        </Typography>
      </Alert>
    </Box>
  );
};

export default EasyMode;
